import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import HermesChat from '../components/hermes/HermesChat';
import PDFViewer from '../components/chat/PDFViewer';
import useHermesSession from '../hooks/useHermesSession';
import { AGENT_NAME } from '../config';
import { createChat, touchChat, refreshChats } from '../lib/hermesChats';

// Standalone Hermes chat page. Stage 2 — baseUrl is the Cloudflare Worker
// (hermes-proxy), brokered per-user via GET /api/agent/hermes/session/.
// Conversations are per-user, identified by URL param /hermes/:convId.
// localStorage tracks the chat-list metadata; Hermes itself persists message
// history server-side, keyed by the same convId via the `conversation` param.
export default function HermesPage() {
  const { convId: urlConvId } = useParams();
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId') || 'anon';
  const { baseUrl, sessionToken, sessionKey, wakeMs, filesBase, loading, error, refresh } = useHermesSession();

  // PDF citation side viewer (plan 206 Part A). Citations in assistant answers
  // open the source PDF here at the cited page.
  const [pdf, setPdf] = useState({ url: null, page: 1, filename: 'Document.pdf', isOpen: false });
  const handleOpenPdf = (url, page = 1, filename = 'Document.pdf') =>
    setPdf({ url, page: Number(page) || 1, filename, isOpen: true });
  const handleClosePdf = () => setPdf((p) => ({ ...p, isOpen: false }));

  // If we landed on /agentbro with no convId, mint one and replace the URL so
  // refresh / share / browser-back keep working. Use an effect (with a ref
  // guard) so Strict Mode's double-invocation doesn't create two chats.
  const creatingRef = useRef(false);
  useEffect(() => {
    if (urlConvId || creatingRef.current) return;
    creatingRef.current = true;
    const created = createChat(userId);
    navigate(`/agentbro/${created.id}`, { replace: true });
  }, [urlConvId, navigate, userId]);
  const convId = urlConvId;

  // Pull the authoritative session list from Hermes whenever a session is live.
  // This is what populates the sidebar on a NEW DEVICE (the localStorage cache is
  // empty there; the server list — durable on the per-user volume — fills it).
  useEffect(() => {
    if (baseUrl && sessionToken) {
      refreshChats(userId, baseUrl, sessionToken).catch(() => { /* best-effort */ });
    }
  }, [baseUrl, sessionToken, userId]);

  // After the first message, the server creates the session; refresh shortly after
  // so the new chat shows its server-generated preview/title in the sidebar.
  const refreshedAfterFirst = useRef(false);
  const handleFirstUserMessage = () => {
    if (!convId) return;
    touchChat(userId, convId);
    if (refreshedAfterFirst.current || !baseUrl || !sessionToken) return;
    refreshedAfterFirst.current = true;
    setTimeout(() => refreshChats(userId, baseUrl, sessionToken).catch(() => {}), 4000);
  };

  if (!convId) {
    // While the effect mints a new chat and replaces the URL.
    return (
      <div className="h-full bg-[#1a1c2a] flex items-center justify-center text-gray-500 text-sm">
        <Loader2 size={20} className="animate-spin mr-2" /> Preparing…
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full bg-[#1a1c2a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Loader2 size={32} className="animate-spin text-[#4F8EF7]" />
          <div className="text-sm">Waking {AGENT_NAME}…</div>
          <div className="text-[11px] text-gray-600">first spawn can take 10–15s</div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-full bg-[#1a1c2a] flex flex-col items-center justify-center text-sm text-gray-300 gap-3">
        <div className="text-red-400">Couldn't reach your assistant: {error}</div>
        <button onClick={refresh} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="h-full bg-[#1a1c2a] relative flex flex-row min-h-0">
      {/* PDF citation viewer: desktop = left split-pane (50%); mobile = full overlay. */}
      {pdf.isOpen && (
        <div className="w-1/2 shrink-0 h-full max-md:fixed max-md:inset-0 max-md:z-30 max-md:w-full">
          <PDFViewer
            url={pdf.url}
            page={pdf.page}
            filename={pdf.filename}
            isOpen={pdf.isOpen}
            onClose={handleClosePdf}
            widthMode="fill"
          />
        </div>
      )}
      <div className="flex-1 min-w-0 relative h-full">
        <HermesChat
          key={convId}        // remount on chat switch — wipes local messages state
          baseUrl={baseUrl}
          apiKey={sessionToken}
          sessionKey={sessionKey}
          filesBase={filesBase}
          conversation={convId}
          onUserMessage={handleFirstUserMessage}
          onSessionStale={refresh}
          onOpenPdf={handleOpenPdf}
        />
        {wakeMs?.total != null && (
          <div className="absolute top-2 right-3 text-[10px] text-gray-600 pointer-events-none">
            woke in {wakeMs.total}ms
          </div>
        )}
      </div>
    </div>
  );
}
